import { StripeSubscriptionStatus, type BillingInfoPayload } from '@/modules/Billing/types'
import { defineStore } from 'pinia'
import { computed } from 'vue'
import { ref } from 'vue'
import {
  annotationCreditsBilledNextPeriod,
  PlanPricing,
  resolvePlanForCredit,
  resolveTrialDaysLeft,
} from './cbbProductUtils'
import { formatUnixDate } from '@/core/utils/time'

export const useBillingStore = defineStore('billing', () => {
  const billingInfoLoading = ref(false)
  const billingInfo = ref<BillingInfoPayload | null>(null)

  const billedNextPeriod = computed(() =>
    billingInfo.value ? annotationCreditsBilledNextPeriod(billingInfo.value) : null,
  )

  const planName = computed(() => {
    if (
      billingInfo.value?.customer.stripe_subscription_status === StripeSubscriptionStatus.Trialing
    ) {
      return 'Trial'
    }

    if (!billedNextPeriod.value || billedNextPeriod.value === 0) {
      return 'Cancelled'
    }

    const plan = resolvePlanForCredit(billedNextPeriod.value)

    // Handle legacy essentials case
    if (plan === 'freemium' && billedNextPeriod.value === PlanPricing.freemium) {
      return 'essentials'
    }

    return plan
  })

  const isLockedOut = computed(() => billingInfo.value?.customer_subscription.locked_out)

  const lockedOutReasons = computed(
    () => billingInfo.value?.customer_subscription.locked_out_reasons ?? [],
  )

  const formattedRenewDate = computed(() => {
    const renewDate = billingInfo.value?.customer.stripe_subscription_period_end
    if (!renewDate) {
      return null
    }
    return formatUnixDate(renewDate, 'D MMM YYYY')
  })

  const isTrialing = computed(
    () =>
      billingInfo.value?.customer.stripe_subscription_status === StripeSubscriptionStatus.Trialing,
  )

  const daysLeftText = computed(() => {
    if (!billingInfo.value) {
      return ''
    }

    const days = resolveTrialDaysLeft(billingInfo.value)
    return `${days} day${days === 1 ? '' : 's'} left`
  })

  const fileStorageLimit = computed(() => {
    if (!billingInfo.value) {
      return null
    }

    return (
      billingInfo.value.customer_subscription.storage_standard_max_in_period +
      billingInfo.value.customer_subscription.storage_extra
    )
  })

  const resetState = (): void => {
    billingInfoLoading.value = false
    billingInfo.value = null
  }

  return {
    billingInfoLoading,
    billingInfo,
    billedNextPeriod,
    planName,
    isLockedOut,
    lockedOutReasons,
    formattedRenewDate,
    isTrialing,
    daysLeftText,
    fileStorageLimit,
    resetState,
  }
})
